import { cursorIsActive } from "@/models/CursorModel";
import { LabelNodeModel } from "@/models/nodes/LabelNodeModel";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";

import { triggerKeyboardInput } from "../handleKeyboardInput";

import type { DocumentModel } from "@/models/DocumentModel";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { NodeModel } from "@/models/nodes/types";
import type { SymbolContainingNode } from "@/models/nodes/utils";
import type { LabelNodePosition } from "@corca/data";

function hasLabels(
  node: NodeModel | undefined,
  side: "left" | "right",
): node is SymbolContainingNode & { symbolNode: SymbolNodeModel } {
  if (node) {
    return !!("symbolNode" in node && node.symbolNode?.hasLabels(side));
  }

  return false;
}

function shouldRemoveNodeLabels(node: NodeModel | undefined, side: "left" | "right") {
  if (!hasLabels(node, side)) {
    return false;
  }

  if ("hasParams" in node && node.hasParams) {
    return false;
  }

  return true;
}

function shouldRemoveCursorParentLabels(
  node: InlineNodeModel | undefined,
  cursorIndex: number | undefined,
  side: "left" | "right",
) {
  return cursorIndex === 0 && node?.index === 1 && hasLabels(node?.parent, side);
}

export function shouldRemoveLabels(doc: DocumentModel, side: "left" | "right"): boolean {
  const { node: cursorNode, index } = doc.cursor;
  let node = doc.cursor.nodeBeforeCursor;

  if (side === "left") {
    node = doc.cursor.nodeAfterCursor;
  }

  return (
    shouldRemoveNodeLabels(node, side) || shouldRemoveCursorParentLabels(cursorNode, index, side)
  );
}

export function getAdjacentLabel(
  doc: DocumentModel,
  position: LabelNodePosition,
): LabelNodeModel | undefined {
  const { nodeBeforeCursor, nodeAfterCursor, node: cursorNode, index } = doc.cursor;
  const side = position.startsWith("left") ? "left" : "right";
  let node = side === "left" ? nodeAfterCursor : nodeBeforeCursor;

  if (side === "right" && index === 0 && cursorNode?.parent) {
    node = cursorNode.parent;
  }

  if (!node || !("symbolNode" in node)) {
    return undefined;
  }

  const labelNode = node.symbolNode?.labelNodes.find((l) => l.position === position);
  return labelNode;
}

export function enterAdjacentLabel(doc: DocumentModel, position: LabelNodePosition): boolean {
  const { inlineNode } = getAdjacentLabel(doc, position) ?? {};

  if (inlineNode) {
    doc.cursor.moveTo(inlineNode, inlineNode.nodes.length);
    return true;
  }

  return false;
}

export function enterAdjacentLabelAndDelete(
  doc: DocumentModel,
  side: "left" | "right",
  event: KeyboardEvent,
) {
  const labelNode = getAdjacentLabel(doc, `${side}-bottom`) || getAdjacentLabel(doc, `${side}-top`);

  if (!labelNode?.inlineNode) return;

  const { node, index } = doc.cursor;
  const key = side === "left" ? "Delete" : "Backspace";
  const labelNodesIndex = side === "left" ? 0 : labelNode.inlineNode.nodes.length;

  doc.cursor.moveTo(labelNode.inlineNode, labelNodesIndex);
  triggerKeyboardInput(doc, key, event);

  if (labelNode.isEmpty) {
    doc.removeNode(labelNode);
    doc.cursor.moveTo(node, index);
  }
}

/**
 * Inserts new label at `position`. If the label at this `position` already exists it moves the cursor to the end of the labels's inline node.
 * @param node
 * @param position
 */
export function insertLabel(
  node: SymbolNodeModel | SymbolContainingNode,
  position: LabelNodePosition,
  moveToLabel = true,
) {
  const {
    doc,
    doc: { cursor },
  } = node;
  const symbolNode = node.is(SymbolNodeModel) ? node : node.symbolNode;

  if (!symbolNode) {
    return undefined;
  }

  let currentLabelNode = symbolNode.labelNodes.find(
    (n) => n.is(LabelNodeModel) && n.props.position === position,
  );

  if (!currentLabelNode) {
    currentLabelNode = doc.create(LabelNodeModel, { position });

    // prepend labels if they visually displayed on left side of symbol node
    // just to make cursor correctly moves between flatNodes on ArrowLeft ArrowRight pressed
    if (currentLabelNode.position === "left-top" || currentLabelNode.position === "left-bottom") {
      symbolNode.prependChild(currentLabelNode);
    } else {
      symbolNode.appendChild(currentLabelNode);
    }
  }

  if (moveToLabel && currentLabelNode && cursorIsActive(cursor)) {
    const { inlineNode } = currentLabelNode;

    if (inlineNode) {
      cursor.moveTo(inlineNode, inlineNode.nodes.length);
      return inlineNode;
    }
  }

  return undefined;
}
